import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AlertTriangle, Download, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Can } from '../../../components/Can';
import AccessRestricted from '../../../components/AccessRestricted';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { PurchaseOrderScope, PurchaseOrderStatus, PurchaseOrderSummary } from '../../../types/procurement.types';
import { Branch, Vendor } from '../../../types/lists.types';
import { PurchaseOrderListParams, purchaseOrdersApi } from '../../../utils/procurement-api';
import { branchesApi, vendorsApi } from '../../../utils/lists-api';
import PurchaseOrderKanban from './components/PurchaseOrderKanban';
import {
    AmendmentChip,
    ClosedShortChip,
    PURCHASE_ORDER_OFF_FLOW_STATUSES,
    PURCHASE_ORDER_STATUS_LABELS,
    PURCHASE_ORDER_STATUS_ORDER,
    PurchaseOrderStatusPill,
    ReceivedProgress,
} from './components/PurchaseOrderStatus';
import { formatDate, money, orderReference, vendorName } from './procurement-ui';
import { cn } from '../../../components/ui/utils';

const ALL = 'all';

/** One screenful of lanes and rows; the register is not paginated, so say when it is cut. */
const REGISTER_LIMIT = 200;

const CHIP_STATUSES: PurchaseOrderStatus[] = [
    ...PURCHASE_ORDER_STATUS_ORDER,
    ...PURCHASE_ORDER_OFF_FLOW_STATUSES,
];

type View = 'list' | 'kanban';

/**
 * Subtitle per server-resolved scope, so the page never claims a wider view than the rows show.
 * The value comes from the API, not from re-deriving the rule here — one implementation.
 */
const SCOPE_SUBTITLE: Record<PurchaseOrderScope, string> = {
    all: 'Requests, PO numbers and receiving at every branch.',
    own: 'Requests you raised — from draft through to goods received.',
    approval: 'Waiting on your decision, across every branch.',
    receiving: 'Deliveries to book in against your yards’ purchase orders.',
    mixed: 'Your own requests, plus the queues your access covers.',
};

const isStatus = (value: string | null): value is PurchaseOrderStatus =>
    !!value && (CHIP_STATUSES as string[]).includes(value);

/**
 * Purchase Orders register — the list/Kanban toggle the client asked for.
 *
 * Both views are driven by one `list()` call: the rows come from `orders`, the lane headers
 * from `counts`, and the Kanban groups the very same array client-side so a lane can never
 * claim a number its cards do not back up.
 *
 * The status filter belongs to the list view only — on the Kanban every lane *is* a status,
 * and applying one there would leave seven empty columns behind a single full one.
 */
export default function PurchaseOrdersPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.PURCHASE_ORDER);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [view, setView] = useState<View>(() =>
        searchParams.get('view') === 'kanban' ? 'kanban' : 'list');
    const [status, setStatus] = useState<string>(() => {
        const fromUrl = searchParams.get('status');
        return isStatus(fromUrl) ? fromUrl : ALL;
    });
    const [search, setSearch] = useState('');
    // Debounced, so a filter change is one request rather than one per keystroke.
    const [query, setQuery] = useState('');
    const [branchId, setBranchId] = useState<string>(() => searchParams.get('branchId') ?? ALL);
    const [vendorId, setVendorId] = useState<string>(ALL);
    const [mineOnly, setMineOnly] = useState(() => searchParams.get('mine') === 'true');
    const [amendmentsOnly, setAmendmentsOnly] = useState(() =>
        searchParams.get('amendment') === 'open');

    const [orders, setOrders] = useState<PurchaseOrderSummary[]>([]);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [total, setTotal] = useState(0);
    /**
     * What the server decided this caller may see. The page's copy follows it, so a Requester
     * is never told they are looking at "every branch" when the rows are their own.
     */
    const [scope, setScope] = useState<PurchaseOrderScope>('all');
    const [branches, setBranches] = useState<Branch[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const timer = window.setTimeout(() => setQuery(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);

    const filters = useMemo<PurchaseOrderListParams>(() => ({
        status: view === 'list' && status !== ALL ? status : undefined,
        branchId: branchId !== ALL ? branchId : undefined,
        vendorId: vendorId !== ALL ? vendorId : undefined,
        mine: mineOnly ? 'true' : undefined,
        amendment: amendmentsOnly ? 'open' : undefined,
        q: query || undefined,
        limit: String(REGISTER_LIMIT),
    }), [view, status, branchId, vendorId, mineOnly, amendmentsOnly, query]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await purchaseOrdersApi.list(filters);
            setOrders(data.orders);
            setCounts(data.counts);
            setTotal(data.total);
            setScope(data.abilities?.scope ?? 'all');
            setError(null);
        } catch (err) {
            // The api client has already toasted this; the message is kept so the screen
            // can explain itself instead of rendering an empty register.
            setError(err instanceof Error && err.message
                ? err.message
                : 'Failed to load purchase orders');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, [filters]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    useEffect(() => {
        if (!canRead) return;
        branchesApi.list({ status: 'active' }).then(setBranches).catch(() => undefined);
        vendorsApi.list().then(setVendors).catch(() => undefined);
    }, [canRead]);

    const handleExport = async () => {
        try {
            setExporting(true);
            // The register caps itself at one screenful; the export must not inherit that cap.
            await purchaseOrdersApi.export('csv', { ...filters, limit: undefined });
            toast.success('Purchase order export downloaded');
        } catch (err) {
            // The export helper talks to the API directly, so there is no shared toast here.
            toast.error(err instanceof Error && err.message
                ? err.message
                : 'Failed to export purchase orders');
        } finally {
            setExporting(false);
        }
    };

    if (!canRead) return <AccessRestricted />;

    const truncated = total > orders.length;

    const statusChip = (value: string, label: string, count?: number) => (
        <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            className={cn(
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium',
                status === value
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-border bg-card text-muted-foreground hover:border-slate-300'
            )}
        >
            {label}
            {count !== undefined && (
                <span className={cn('tabular-nums', status === value ? 'text-white/70' : 'text-muted-foreground')}>
                    {count}
                </span>
            )}
        </button>
    );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Purchase Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {SCOPE_SUBTITLE[scope]}
                    </p>
                </div>
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                    <Can I={ACTIONS.EXPORT} a={RESOURCES.PURCHASE_ORDER}>
                        <Button
                            variant="outline"
                            onClick={handleExport}
                            disabled={exporting}
                            className="flex items-center gap-2 w-full md:w-auto"
                        >
                            <Download size={16} />
                            <span>{exporting ? 'Exporting…' : 'Export CSV'}</span>
                        </Button>
                    </Can>
                    <Can I={ACTIONS.CREATE} a={RESOURCES.PURCHASE_ORDER}>
                        <Button
                            onClick={() => navigate('/stock/procurement/purchase-orders/new')}
                            className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                        >
                            <Plus size={16} /><span>New purchase order</span>
                        </Button>
                    </Can>
                </div>
            </div>

            <Tabs value={view} onValueChange={(v) => setView(v as View)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="list" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        List
                    </TabsTrigger>
                    <TabsTrigger value="kanban" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Kanban
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="space-y-3">
                <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                    <div className="relative w-full md:flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by reference, PO number or vendor..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 w-full"
                        />
                    </div>
                    <div className="w-full md:w-[190px]">
                        <Select value={branchId} onValueChange={setBranchId}>
                            <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All branches</SelectItem>
                                {branches.map((b) => (
                                    <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-full md:w-[190px]">
                        <Select value={vendorId} onValueChange={setVendorId}>
                            <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ALL}>All vendors</SelectItem>
                                {vendors.map((v) => (
                                    <SelectItem key={v.id} value={String(v.id)}>
                                        {v.tradingName || v.legalName}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                    {/* Hidden for a Requester: the server already restricts them to their own
                        records, so the switch could only ever be a no-op. */}
                    {scope !== 'own' && (
                        <div className="flex items-center gap-3">
                            <Switch
                                checked={mineOnly}
                                onCheckedChange={setMineOnly}
                                aria-label="Mine only"
                            />
                            <span className="text-sm text-muted-foreground">Mine only</span>
                        </div>
                    )}
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={amendmentsOnly}
                            onCheckedChange={setAmendmentsOnly}
                            aria-label="Amendments waiting"
                        />
                        <span className="text-sm text-muted-foreground">Amendments waiting</span>
                    </div>
                </div>

                {view === 'list' ? (
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-2 px-2 md:mx-0 md:px-0">
                        {statusChip(ALL, 'All', total)}
                        {CHIP_STATUSES.map((s) => statusChip(
                            s, PURCHASE_ORDER_STATUS_LABELS[s], counts[s] ?? 0,
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">
                        Every status is a lane. Switch to List to filter down to one.
                    </p>
                )}
            </div>

            {!loading && !error && (
                <p className="text-sm text-muted-foreground">
                    {total} record{total === 1 ? '' : 's'}
                    {truncated && ` · showing the first ${orders.length}, narrow the filters to see more`}
                </p>
            )}

            {error && orders.length > 0 && (
                <p className="text-sm text-amber-700">
                    Showing the last results — {error}
                </p>
            )}

            {loading && !hasLoaded ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : error && orders.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle size={28} className="mx-auto text-destructive mb-3" />
                        <p className="font-medium">Could not load purchase orders</p>
                        <p className="text-sm text-muted-foreground mt-1">{error}</p>
                        <Button variant="outline" className="mt-4" onClick={load}>Try again</Button>
                    </CardContent>
                </Card>
            ) : orders.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <p className="font-medium">
                            {scope === 'own'
                                ? 'You have not raised any purchase orders yet'
                                : 'No purchase orders match these filters'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                            {scope === 'own'
                                ? 'Raise one with New purchase order — it saves as a draft until you submit it.'
                                : 'Try another status, branch or vendor — or clear the search.'}
                        </p>
                    </CardContent>
                </Card>
            ) : view === 'kanban' ? (
                <>
                    <PurchaseOrderKanban orders={orders} counts={counts} />
                    {truncated && (
                        <p className="text-xs text-muted-foreground">
                            Lane counts cover every matching order; only the first {orders.length} are shown as cards.
                        </p>
                    )}
                </>
            ) : (
                <>
                    {/* Mobile — one card per order, no sideways scrolling */}
                    <div className="md:hidden space-y-3">
                        {orders.map((o) => (
                            <Card
                                key={o.id}
                                className="p-4 active:bg-accent/40"
                                onClick={() => navigate(`/stock/procurement/purchase-orders/${o.id}`)}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="font-mono font-medium">{orderReference(o)}</span>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">
                                            {vendorName(o.vendor)}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {o.branch.name}{o.project ? ` · ${o.project.name}` : ''}
                                        </p>
                                    </div>
                                    <PurchaseOrderStatusPill status={o.status} />
                                </div>

                                <div className="mt-3 flex items-center justify-between gap-2">
                                    <span className="text-sm font-semibold tabular-nums">{money(o.totalValue)}</span>
                                    <ReceivedProgress percent={o.receivedPercent} />
                                </div>

                                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                    <span>Required {formatDate(o.requiredByDate)}</span>
                                    {o.closedShort && <ClosedShortChip />}
                                </div>

                                {o.openAmendmentCount > 0 && (
                                    <div className="mt-2">
                                        <AmendmentChip count={o.openAmendmentCount} />
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Ref</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Vendor</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Branch</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Project</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Required by</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Total</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Received</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Amendments</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {orders.map((o) => (
                                        <TableRow
                                            key={o.id}
                                            onClick={() => navigate(`/stock/procurement/purchase-orders/${o.id}`)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="font-mono whitespace-nowrap">
                                                {orderReference(o)}
                                            </TableCell>
                                            <TableCell>{vendorName(o.vendor)}</TableCell>
                                            <TableCell className="whitespace-nowrap">{o.branch.name}</TableCell>
                                            <TableCell>
                                                {o.project?.name ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {formatDate(o.requiredByDate)}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums whitespace-nowrap">
                                                {money(o.totalValue)}
                                            </TableCell>
                                            <TableCell>
                                                <ReceivedProgress percent={o.receivedPercent} />
                                            </TableCell>
                                            <TableCell>
                                                <AmendmentChip count={o.openAmendmentCount} />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col items-start gap-1">
                                                    <PurchaseOrderStatusPill status={o.status} />
                                                    {o.closedShort && <ClosedShortChip />}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
